import React, { useEffect, useState } from 'react';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { DataService } from '../../services/data-service';
import Loader from 'src/components/Loader';
import { toast } from 'react-toastify';
import DownloadIcon from '@mui/icons-material/Download';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import type { Brand, StorageTypeResponse, TypesApiResponse } from 'src/types/Brand.types';
import PermissionService, { ACTION, ROLE } from 'src/services/permission-service';
import { Delete } from '@mui/icons-material';
import { OpenConfirmation } from 'src/redux/confirmation/confirmationReducer';
import { showAlert } from 'src/redux/alert/alertReducer';

const TypesListing: React.FC = () => {
  const [PreDefinedVariables, setPreDefinedVariables] = useState<Record<string, Brand>>({});
  const [loadingPreDefinedVariables, setLoadingPreDefinedVariables] = useState(true);
  const [openAddProductType, setOpenAddProductType] = useState(false);
  const [openEditProductType, setOpenEditProductType] = useState(false);
  const [updateProductType, setUpdateProductType] = useState<Brand | undefined>(undefined);

  const [storageTypes, setStorageTypes] = useState<Record<string, Brand>>({});
  const [loadingStorageTypes, setLoadingStorageTypes] = useState(true);
  const [openAddStorageType, setOpenAddStorageType] = useState(false);
  const [openEditStorageType, setOpenEditStorageType] = useState(false);
  const [updateStorageType, setUpdateStorageType] = useState<Brand | undefined>(undefined);

  const userRole = useAppSelector(s => s.user.user.role) as ROLE;
  const dispatch = useAppDispatch();

  const initializePreDefinedVariables = async () => {
    setLoadingPreDefinedVariables(true);
    try {
      const res = await DataService.get('api/PreDefinedVariable');
      const response: TypesApiResponse = await res.json();
      const data: StorageTypeResponse[] = response.data || [];
      const result: Record<string, Brand> = {};
      data.forEach((w) => {
        result[w.id] = { 
          Id: w.id,
          Name: w.name 
        };
      });
      setPreDefinedVariables(result);
    } catch (e) {
      toast.error('Failed to fetch Product Types');
    }
    setLoadingPreDefinedVariables(false);
  };

  const initializeStorageTypes = async () => {
    setLoadingStorageTypes(true);
    try {
      const res = await DataService.get('api/StorageType');
      const response: TypesApiResponse = await res.json();
      const data: StorageTypeResponse[] = response.data || [];
      const result: Record<string, Brand> = {};
      data.forEach((w) => {
        result[w.id] = { 
          Id: w.id,
          Name: w.name 
        };
      });
      setStorageTypes(result);
    } catch (e) {
      toast.error('Failed to fetch Storage Types');
    }
    setLoadingStorageTypes(false);
  };

  useEffect(() => {
    initializePreDefinedVariables();
    initializeStorageTypes();
  }, []);

  const handleExportPreDefinedVariables = async () => {
    try {
      const response = await DataService.get('api/PreDefinedVariable/export', {});
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'PreDefinedVariables_export.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Export failed');
      }
    } catch {
      toast.error('Export failed');
    }
  };

  const handleExportStorageTypes = async () => {
    try {
      const response = await DataService.get('api/StorageType/export', {});
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'StorageTypes_export.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Export failed');
      }
    } catch {
      toast.error('Export failed');
    }
  };

  // Table headers
  const headersProductType: EnhancedTableColumn[] = [
    {
      id: 'Name',
      label: 'Name',
      type: EnhancedTableColumnType.TEXT,
      editable: true,
      required: true,
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.ACTION,
      actions: [
        {
          onClick: (id: string) => {
            dispatch(OpenConfirmation({
              open: true,
              title: 'Delete PreDefined Variable',
              message: 'Are you sure you want to delete this PreDefined Variable?',
              onSubmit: async () => {
                try {
                  const response = await DataService.delete(`api/PreDefinedVariable/${id}`, {});
                  const data = await response.json();

                  if (response.ok && (data.Success === true || data.success === true)) {
                    dispatch(showAlert({ message: 'PreDefined Variable deleted successfully', type: 'success' }));
                    initializePreDefinedVariables();
                  } else {
                    const errorMessage = data.Message || data.message || 'Failed to delete PreDefined Variable';
                    dispatch(showAlert({ message: errorMessage, type: 'error' }));
                  }
                } catch (error) {
                  dispatch(showAlert({ message: 'Failed to delete PreDefined Variable', type: 'error' }));
                }
              }
            }));
          },
          icon: <Delete fontSize="small" />,
          label: 'Delete',
          visible: () => PermissionService.getInstance().isAllowed(ACTION.VIEW_ALL_SUPPLIERS, userRole),
        }
      ]
    } as EnhancedTableColumn
  ];

  const headersStorageType: EnhancedTableColumn[] = [
    {
      id: 'Name',
      label: 'Name',
      type: EnhancedTableColumnType.TEXT,
      editable: true,
      required: true,
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.ACTION,
      actions: [
        {
          onClick: (id: string) => {
            dispatch(OpenConfirmation({
              open: true,
              title: 'Delete Storage Type',
              message: 'Are you sure you want to delete this Storage Type?',
              onSubmit: async () => {
                try {
                  const response = await DataService.delete(`api/StorageType/${id}`, {});
                  const data = await response.json();

                  if (response.ok && (data.Success === true || data.success === true)) {
                    dispatch(showAlert({ message: 'Storage Type deleted successfully', type: 'success' }));
                    initializeStorageTypes();
                  } else {
                    const errorMessage = data.Message || data.message || 'Failed to delete Storage Type';
                    dispatch(showAlert({ message: errorMessage, type: 'error' }));
                  }
                } catch (error) {
                  dispatch(showAlert({ message: 'Failed to delete Storage Type', type: 'error' }));
                }
              }
            }));
          },
          icon: <Delete fontSize="small" />,
          label: 'Delete',
          visible: () => PermissionService.getInstance().isAllowed(ACTION.VIEW_ALL_SUPPLIERS, userRole),
        }
      ]
    } as EnhancedTableColumn
  ];

  const actionsProductType: ITableMenuAction[] = [
    {
      key: 'export',
      title: '',
      icon: <DownloadIcon />,
      disabled: (selected: string[]) => false,
      onClick: handleExportPreDefinedVariables,
    }
  ];

  const actionsStorageType: ITableMenuAction[] = [
    {
      key: 'export',
      title: '',
      icon: <DownloadIcon />,
      disabled: (selected: string[]) => false,
      onClick: handleExportStorageTypes,
    }
  ];

  const dataObjProductType: Record<string, any> = Object.fromEntries(
    Object.entries(PreDefinedVariables).map(([id, item]) => [
      id,
      {
        ...item,
      }
    ])
  );

  const dataObjStorageType: Record<string, any> = Object.fromEntries(
    Object.entries(storageTypes).map(([id, item]) => [
      id,
      {
        ...item,
      }
    ])
  );

  const handleAddProductType = async (row: { Name: string }) => {
    const req = await DataService.post('api/PreDefinedVariable/create', { Name: row.Name });
    const resJson = await req.json();
    if (req.ok && resJson.success) {
      await initializePreDefinedVariables();
      return true;
    } else {
      throw new Error(resJson.message || 'An Error occurred!');
    }
  };

  const handleEditProductType = async (row: { id: string, Name: string }) => {
    const req = await DataService.put(`api/PreDefinedVariable/update/${row.id}`, {id: row.id, Name: row.Name });
    const resJson = await req.json();
    if (req.ok && resJson.success) {
      await initializePreDefinedVariables();
      return true;
    } else {
      throw new Error(resJson.message || 'An Error occurred!');
    }
  };

  const handleAddStorageType = async (row: { Name: string }) => {
    const req = await DataService.post('api/StorageType/create', { Name: row.Name });
    const resJson = await req.json();
    if (req.ok && resJson.success) {
      await initializeStorageTypes();
      return true;
    } else {
      throw new Error(resJson.message || 'An Error occurred!');
    }
  };

  const handleEditStorageType = async (row: { id: string, Name: string }) => {
    const req = await DataService.put(`api/StorageType/update/${row.id}`, { Name: row.Name });
    const resJson = await req.json();
    if (req.ok && resJson.success) {
      await initializeStorageTypes();
      return true;
    } else {
      throw new Error(resJson.message || 'An Error occurred!');
    }
  };

  if (loadingPreDefinedVariables || loadingStorageTypes) {
    return <Loader />;
  }

  return (
    <div>
      <title>Types</title>
      <div  data-testid="predefined-table" style={{ marginBottom: 48 }}>
        <EnhancedTable
          title="Predefined Variables"
          striped={true}
          wmsMode={true}
          columns={headersProductType}
          data={dataObjProductType}
          actions={actionsProductType}
          onAddRow={handleAddProductType}
          onEditRow={handleEditProductType}
          showEditOnTable={true}
          collapsible={true}
          densityLevel="compact"
        />
      </div>
      <div data-testid="storage-table">
        <EnhancedTable
          title="Storage Types"
          columns={headersStorageType}
          data={dataObjStorageType}
          actions={actionsStorageType}
          onAddRow={handleAddStorageType}
          onEditRow={handleEditStorageType}
          showEditOnTable={true}
          collapsible={true}
          striped={true}
          wmsMode={true}
          densityLevel="compact"

        />
      </div>
    </div>
  );
};

export default TypesListing; 